/**
 * Helper to fetch product IDs from SKUs
 *
 * Uses the plugin's dedicated lookup endpoint, which resolves SKUs with a
 * direct meta query instead of paging the whole catalogue through the core
 * REST API.
 */

import apiFetch from '@wordpress/api-fetch';

// Cache for SKU to ID lookups
const skuToIdCache = new Map();

/**
 * Resolve SKUs to product IDs via the lookup endpoint, filling the cache.
 *
 * @param {Array<string>} skus SKUs not yet cached.
 * @return {Promise<void>}
 */
const lookupSkus = async skus => {
	if ( skus.length === 0 ) {
		return;
	}

	try {
		const map = await apiFetch( {
			path: `/at-configurator/v1/product-ids?skus=${ encodeURIComponent( skus.join( ',' ) ) }`,
		} );

		skus.forEach( sku => {
			skuToIdCache.set( sku, map?.[ sku ] ?? null );
		} );
	} catch ( error ) {
		skus.forEach( sku => skuToIdCache.set( sku, null ) );
	}
};

/**
 * Get product ID from SKU
 *
 * @param {string} sku Product SKU
 * @return {Promise<number|null>} Product ID or null if not found
 */
export const getProductIdFromSku = async sku => {
	if ( ! sku ) {
		return null;
	}

	if ( ! skuToIdCache.has( sku ) ) {
		await lookupSkus( [ sku ] );
	}

	return skuToIdCache.get( sku );
};

/**
 * Get product IDs from SKUs
 *
 * @param {Array<string>} skus Array of SKUs
 * @return {Promise<Array<number>>} Array of product IDs (nulls filtered out)
 */
export const getProductIdsFromSkus = async skus => {
	if ( ! skus || skus.length === 0 ) {
		return [];
	}

	await lookupSkus( skus.filter( sku => sku && ! skuToIdCache.has( sku ) ) );

	return skus
		.map( sku => skuToIdCache.get( sku ) )
		.filter( id => id !== null && id !== undefined && ! isNaN( id ) );
};

/**
 * Check if a string is a numeric ID or a SKU
 *
 * @param {string} value Value to check
 * @return {boolean} True if numeric (ID), false if SKU
 */
export const isNumericId = value => {
	return /^\d+$/.test( value );
};
